'use client';
import { useState, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import { stories } from '@/lib/data';
import Pagination from '@/components/ui/Pagination';
import Link from 'next/link';

function highlightMatch(text: string, term: string): string {
  if (!term.trim()) return text;
  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return text.replace(new RegExp(`(${escaped})`, 'gi'), '<mark>$1</mark>');
}

const suggestedSearches = ['Geneva ceasefire', 'AI regulation', 'Climate change', 'Federal Reserve', 'Ukraine war', 'US elections'];

function SearchContent() {
  const searchParams = useSearchParams();
  const initialQuery = searchParams.get('q') || '';

  const [query, setQuery] = useState(initialQuery);
  const [inputValue, setInputValue] = useState(initialQuery);
  const [activeSection, setActiveSection] = useState('All sections');
  const [activeDate, setActiveDate] = useState('Any time');
  const [activeType, setActiveType] = useState('All types');
  const [sortBy, setSortBy] = useState('Relevance');
  const [currentPage, setCurrentPage] = useState(1);
  const [activePills, setActivePills] = useState<string[]>([]);

  const results = query.trim()
    ? stories.filter(s =>
        s.title.toLowerCase().includes(query.toLowerCase()) ||
        s.deck.toLowerCase().includes(query.toLowerCase()) ||
        s.tags.some(t => t.toLowerCase().includes(query.toLowerCase()))
      )
    : stories;

  const doSearch = () => {
    setQuery(inputValue);
    setCurrentPage(1);
  };

  const removePill = (pill: string) => {
    setActivePills(prev => prev.filter(p => p !== pill));
  };

  return (
    <div className="max-w-[1400px] mx-auto px-5 py-8">
      <h1 className="font-['Playfair_Display',Georgia,serif] font-bold text-[30px] mb-5">Search</h1>

      {/* SEARCH BAR */}
      <div className="flex border-2 border-[#0D0D0D] mb-7">
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && doSearch()}
          placeholder="Search topics, stories, authors..."
          className="flex-1 px-4 py-3.5 font-['IBM_Plex_Sans',system-ui,sans-serif] text-[17px] text-[#0D0D0D] outline-none bg-transparent"
          id="search-input"
        />
        <button
          onClick={doSearch}
          className="bg-[#1A1A1A] text-white px-6 font-['IBM_Plex_Sans',system-ui,sans-serif] font-semibold text-[14px] hover:bg-[#C8102E] transition-colors"
          id="search-submit"
        >
          Search
        </button>
      </div>

      {/* Results count */}
      {query && (
        <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[13px] text-[#6B6B6B] mb-5">
          <span className="font-bold text-[#0D0D0D]">{results.length.toLocaleString()}</span> results for &apos;<span className="font-semibold">{query}</span>&apos;
        </p>
      )}

      {/* FILTER ROW */}
      <div className="flex flex-wrap gap-3 mb-4">
        {[
          { label: 'Section', value: activeSection, setter: setActiveSection, options: ['All sections', 'World', 'Politics', 'Business', 'Technology', 'Climate', 'Sport', 'Culture'] },
          { label: 'Date', value: activeDate, setter: setActiveDate, options: ['Any time', 'Last 24 hours', 'Last 7 days', 'Last 30 days', 'Last year'] },
          { label: 'Type', value: activeType, setter: setActiveType, options: ['All types', 'Articles', 'Videos', 'Opinion', 'Analysis', 'Explainers'] },
          { label: 'Sort', value: sortBy, setter: setSortBy, options: ['Relevance', 'Newest', 'Most Read'] },
        ].map(filter => (
          <select
            key={filter.label}
            value={filter.value}
            onChange={(e) => filter.setter(e.target.value)}
            className="border border-[#D4D4D4] px-3 py-2 font-['IBM_Plex_Sans',system-ui,sans-serif] text-[13px] text-[#2D2D2D] bg-white outline-none cursor-pointer"
            aria-label={filter.label}
          >
            {filter.options.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        ))}
      </div>

      {/* ACTIVE FILTER PILLS */}
      {activePills.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {activePills.map(pill => (
            <span key={pill} className="bg-[#1A1A1A] text-white px-3 py-1 text-[12px] font-['IBM_Plex_Sans',system-ui,sans-serif] flex items-center gap-2">
              {pill}
              <button
                onClick={() => removePill(pill)}
                className="text-[#9A9A9A] hover:text-white ml-1 leading-none"
                aria-label={`Remove ${pill} filter`}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}

      {/* RESULTS */}
      {results.length === 0 ? (
        <div className="py-16 text-center border border-[#D4D4D4] max-w-[600px]">
          <p className="font-['Playfair_Display',Georgia,serif] text-[24px] mb-4">No results found for &apos;{query}&apos;</p>
          <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[14px] text-[#6B6B6B] mb-6">Try a different search term or browse our sections.</p>
          <div className="mb-2">
            <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] text-[#6B6B6B] uppercase tracking-[0.06em]">Suggested searches:</span>
          </div>
          <div className="flex flex-wrap gap-2 justify-center mt-3">
            {suggestedSearches.map(s => (
              <button
                key={s}
                onClick={() => { setInputValue(s); setQuery(s); }}
                className="border border-[#D4D4D4] px-3 py-1.5 text-[13px] font-['IBM_Plex_Sans',system-ui,sans-serif] text-[#2D2D2D] hover:bg-[#1A1A1A] hover:text-white hover:border-[#1A1A1A] transition-all"
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div>
          {results.slice(0, 10).map(result => (
            <Link key={result.id} href={`/article/${result.slug}`}>
              <div className="grid grid-cols-[1fr_140px] gap-5 py-5 border-b border-[#D4D4D4] hover:opacity-75 transition-opacity cursor-pointer">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[11px] tracking-[0.1em] uppercase text-[#C8102E]">
                      {result.section}
                    </span>
                    {result.label && ['analysis', 'opinion', 'explainer', 'feature'].includes(result.label) && (
                      <span className={`font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[10px] tracking-[0.1em] uppercase px-1.5 py-0.5 text-white ${
                        result.label === 'analysis' ? 'bg-[#1A1A1A]' :
                        result.label === 'opinion' ? 'bg-[#1A4A8A]' :
                        result.label === 'explainer' ? 'bg-[#0C7A6E]' : 'bg-[#E07B00]'
                      }`}>{result.label.toUpperCase()}</span>
                    )}
                  </div>
                  <h2
                    className="font-['Playfair_Display',Georgia,serif] font-bold text-[20px] leading-[1.3] mt-1 mb-2"
                    dangerouslySetInnerHTML={{ __html: highlightMatch(result.title, query) }}
                  />
                  <p
                    className="font-['Source_Serif_4',Georgia,serif] text-[14px] text-[#2D2D2D] leading-[1.5] line-clamp-2 mb-2"
                    dangerouslySetInnerHTML={{ __html: highlightMatch(result.deck, query) }}
                  />
                  <div className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] text-[#6B6B6B]">
                    {result.section} · {result.publishedAt} · {result.author} · {result.readingTime} min read
                  </div>
                </div>
                <div className={`${result.imageBg} h-[80px] w-full`} />
              </div>
            </Link>
          ))}
          <Pagination currentPage={currentPage} totalPages={10} onPageChange={setCurrentPage} />
        </div>
      )}
    </div>
  );
}

export default function SearchPage() {
  return (
    <Suspense fallback={<div className="max-w-[1400px] mx-auto px-5 py-8"><p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[14px] text-[#6B6B6B]">Loading search...</p></div>}>
      <SearchContent />
    </Suspense>
  );
}
